import React from "react";
import { useForm } from "react-hook-form";

const IncidentReportingModal = ({ isOpen, onClose, onSubmit, studentId, studentName, incidentToEdit }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const handleFormSubmit = (data) => {
    // Add studentId to the submitted data
    const incidentData = {
      ...data,
      studentId,
      reportDate: new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format
    };
    
    onSubmit(incidentData);
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Report Incident for {studentName}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
            <div>
              <label className="block">Reporter Name</label>
              <input
                type="text"
                {...register("reporterName", { required: "Reporter name is required" })}
                className="w-full p-2 border rounded"
                placeholder="Enter reporter name"
              />
              {errors.reporterName && <span className="text-red-500 text-sm">{errors.reporterName.message}</span>}
            </div>

            <div>
              <label className="block">Reporter Type</label>
              <select
                {...register("reporterType", { required: "Reporter type is required" })}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Reporter Type</option>
                <option value="Student">Student</option>
                <option value="Teacher">Teacher</option>
                <option value="Admin">Admin</option>
                <option value="Parent">Parent</option>
              </select>
              {errors.reporterType && <span className="text-red-500 text-sm">{errors.reporterType.message}</span>}
            </div>

            <div>
              <label className="block">Date & Time of Incident</label>
              <input
                type="datetime-local"
                {...register("dateTime", { required: "Date and time is required" })}
                className="w-full p-2 border rounded"
              />
              {errors.dateTime && <span className="text-red-500 text-sm">{errors.dateTime.message}</span>}
            </div>

            <div>
              <label className="block">Location</label>
              <select
                {...register("location", { required: "Location is required" })}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Location</option>
                <option value="Classroom">Classroom</option>
                <option value="Playground">Playground</option>
                <option value="Hallway">Hallway</option>
                <option value="Online">Online</option>
                <option value="Other">Other</option>
              </select>
              {errors.location && <span className="text-red-500 text-sm">{errors.location.message}</span>}
            </div>

            <div>
              <label className="block">Category</label>
              <select
                {...register("category", { required: "Category is required" })}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Category</option>
                <option value="Bullying">Bullying</option>
                <option value="Misconduct">Misconduct</option>
                <option value="Technical Issue">Technical Issue</option>
                <option value="Safety Concern">Safety Concern</option>
                <option value="Health Issue">Health Issue</option>
                <option value="Other">Other</option>
              </select>
              {errors.category && <span className="text-red-500 text-sm">{errors.category.message}</span>}
            </div>

            <div className="col-span-2">
              <label className="block">Incident Description</label>
              <textarea
                {...register("description", { required: "Description is required" })}
                className="w-full p-2 border rounded"
                rows="4"
                placeholder="Provide detailed description of the incident"
              />
              {errors.description && <span className="text-red-500 text-sm">{errors.description.message}</span>}
            </div>

            <div>
              <label className="block">Involved Parties</label>
              <input
                type="text"
                {...register("involvedParties")}
                className="w-full p-2 border rounded"
                placeholder="Enter names separated by commas"
              />
            </div>

            <div>
              <label className="block">Severity Level</label>
              <select
                {...register("severityLevel", { required: "Severity level is required" })}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Severity</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
              {errors.severityLevel && <span className="text-red-500 text-sm">{errors.severityLevel.message}</span>}
            </div>

            <div>
              <label className="block">Attachments</label>
              <input
                type="file"
                {...register("attachments")}
                className="w-full p-2 border rounded"
                multiple
              />
            </div>

            <div>
              <label className="block">Action Taken</label>
              <select
                {...register("actionTaken", { required: "Action taken is required" })}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Action</option>
                <option value="Warning Issued">Warning Issued</option>
                <option value="Counseling Provided">Counseling Provided</option>
                <option value="Parental Involvement">Parental Involvement</option>
                <option value="Disciplinary Action">Disciplinary Action</option>
                <option value="Other">Other</option>
              </select>
              {errors.actionTaken && <span className="text-red-500 text-sm">{errors.actionTaken.message}</span>}
            </div>

            <div>
              <label className="block">Assigned To</label>
              <select
                {...register("assignedTo")}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Staff Member</option>
                <option value="staff1">Staff Member 1</option>
                <option value="staff2">Staff Member 2</option>
                <option value="staff3">Staff Member 3</option>
              </select>
            </div>

            <div>
              <label className="block">Status</label>
              <select
                {...register("status")}
                className="w-full p-2 border rounded"
              >
                <option value="Reported">Reported</option>
                <option value="Under Investigation">Under Investigation</option>
                <option value="Resolved">Resolved</option>
                <option value="Escalated">Escalated</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block">Comments/Notes</label>
              <textarea
                {...register("comments")}
                className="w-full p-2 border rounded"
                rows="3"
                placeholder="Additional remarks..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#241763]/80"
            >
              Submit Report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncidentReportingModal;